/**
 * HOOK: useCaseData
 *
 * Gestiona el estado del caso jubilatorio: expediente, filas de historial
 * laboral (rows, rawRows, rowsEditadas), normalizadores y operaciones
 * de edición sobre filas.
 *
 * Extraído de App.jsx como parte de refactor convivencia.
 * Fase 1: solo core state — mutationsLog, processingPayload y
 * engine-related state se extraen en fases posteriores.
 *
 * Convivencia: este hook corre en paralelo con el código inline de App.jsx.
 * Las funciones exportadas a nivel módulo son puras y pueden ser reutilizadas
 * por ambos lados.
 */
/* ------------------------------------------------------------------ */
/*  Pure utility functions (standalone exports)                        */
/* ------------------------------------------------------------------ */

import { parseMonedaLocale } from '../utils/sanitizacion'

// No new imports needed — regimen fields come from parser or default to 'COMUN'

export function normalizePeriodoKey(row) {
  const pk = String(row?.periodoKey || '').trim()
  if (/^\d{4}-\d{2}$/.test(pk)) return pk
  const iso = String(row?.periodoISO || '').trim()
  if (/^\d{4}-\d{2}$/.test(iso)) return iso
  const per = String(row?.periodo || '').trim()
  const slash = per.match(/^(\d{2})\/(\d{4})$/)
  if (slash) return `${slash[2]}-${slash[1]}`
  return ''
}

export function parseMontoValue(value) {
  return parseMonedaLocale(value, 0)
}

export function toYmNum(key) {
  const m = String(key || '').match(/^(\d{4})-(\d{2})$/)
  if (!m) return 0
  return Number(`${m[1]}${m[2]}`)
}

export function ymNumToKey(ym) {
  const raw = String(ym || '')
  if (!/^\d{6}$/.test(raw)) return ''
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}`
}

export function toPeriodoKeyFromAny(item) {
  if (!item) return ''
  const directo = normalizePeriodoKey(item)
  if (directo) return directo
  const ano = Number(item.ano)
  const mes = Number(item.mes)
  if (ano >= 1900 && ano <= 2100 && mes >= 1 && mes <= 12) {
    return `${ano}-${String(mes).padStart(2, '0')}`
  }
  return ''
}

export function toActividadKeySet(items) {
  const set = new Set()
  ;(Array.isArray(items) ? items : []).forEach((item) => {
    const key = toPeriodoKeyFromAny(item)
    if (key) set.add(key)
  })
  return set
}

export function getActividadYm(items, options = {}) {
  const requirePositive = Boolean(options.requirePositive)
  const requireValidated = Boolean(options.requireValidated)
  const values = []
  ;(Array.isArray(items) ? items : []).forEach((item) => {
    const key = toPeriodoKeyFromAny(item)
    const ym = toYmNum(key)
    if (!ym) return
    const monto = parseMontoValue(item?.monto ?? item?.importe ?? item?.aporte ?? item?.valor ?? 0)
    const hasPositive = Number.isFinite(monto) ? monto > 0 : true
    const validado = item?.validado
    const isValidated = validado === undefined ? true : Boolean(validado)
    if (requirePositive && !hasPositive) return
    if (requireValidated && !isValidated) return
    values.push(ym)
  })
  return values
}

export function labelActividadMes(hasAutonomo, hasDesempleo) {
  if (hasAutonomo && hasDesempleo) return 'Aporte Monotributo/Autonomo + Desempleo'
  if (hasAutonomo) return 'Aporte Monotributo/Autonomo'
  if (hasDesempleo) return 'Aporte Desempleo'
  return 'MES NO REGISTRADO EN ORIGEN (ANSES.PDF)'
}

export function extractPdfHeaderCese(data) {
  const candidates = [
    data?.fechaCese,
    data?.fecha_cese,
    data?.header?.fechaCese,
    data?.header?.fecha_cese,
    data?.metadata?.fechaCese,
    data?.metadata?.fecha_cese,
    data?.etlSummary?.fechaCese,
    data?.etlSummary?.fecha_cese,
    data?.debug?.fechaCese,
    data?.debug?.fecha_cese,
  ]
  for (const value of candidates) {
    const key = normalizePeriodoKey({ periodo: value, periodoISO: value, periodoKey: value })
    if (key) return key
    const maybeDate = String(value || '').trim().slice(0, 7)
    if (/^\d{4}-\d{2}$/.test(maybeDate)) return maybeDate
  }
  return ''
}

export function toFechaInputFromPeriodoKey(key) {
  const txt = String(key || '').trim()
  const m = txt.match(/^(\d{4})-(\d{2})$/)
  if (!m) return ''
  const year = Number(m[1])
  const month = Number(m[2])
  if (!(year >= 1900 && year <= 2100 && month >= 1 && month <= 12)) return ''
  const lastDay = new Date(year, month, 0).getDate()
  return `${m[1]}-${m[2]}-${String(lastDay).padStart(2, '0')}`
}

export function normalizeReviewRows(inputRows) {
  const arr = Array.isArray(inputRows) ? inputRows : []
  return arr
    .map((r) => {
      const key = normalizePeriodoKey(r)
      const empresa = String(r.empresa || r.razonSocial || r.empleador || '').trim()
      const cuit = String(r.cuit || r.cuil || '').trim()
      const monto = parseMontoValue(r.monto ?? r.remImpSS ?? r.montoOriginal ?? 0)
      const montoDeclarado = parseMontoValue(r.montoDeclarado ?? monto)
      const incluyeSac = Boolean(r.incluyeSac)
      const sacManual = parseMontoValue(r.sacManual || 0)
      const revisionReasons = Array.isArray(r.revisionReasons) ? r.revisionReasons : []
      const amountSource = String(r.amountSource || '')
      const hasCriticalReason = revisionReasons.some((reason) =>
        ['plausibility_outlier_cuit', 'outlier_vs_prev', 'outlier_vs_next'].includes(String(reason || ''))
      )
      const needsVerification = Boolean(
        r.requiresValidation ||
        r.requiereRevision ||
        amountSource.includes('line_') ||
        Number(r.amountConfidence || 0) < 0.8
      )
      const reviewSeverity = hasCriticalReason
        ? 'critical'
        : needsVerification
          ? 'verify'
          : 'ok'
      const diasOrigenRaw = r.diasOrigen ?? r.diasComputables ?? r.dias
      const diasOrigen = Number.isFinite(Number(diasOrigenRaw)) ? Math.trunc(Number(diasOrigenRaw)) : null
      const diasComputables = Number.isFinite(Number(r.diasComputables))
        ? Math.trunc(Number(r.diasComputables))
        : (diasOrigen === 30 ? 30 : (diasOrigen && diasOrigen > 0 && diasOrigen < 30 ? diasOrigen : null))
      const reglaAplicadaDias = String(r.reglaAplicadaDias || r.reglaAplicada || '').trim()
      const tipoTag = String(r.tipoActividad || r.tipo || r.tipoRaw || '').toLowerCase()
      const regimenTag = String(r.regimenContributivo || '').toUpperCase()
      const isMoratoriaRow =
        tipoTag.includes('moratoria') ||
        Boolean(r.esMoratoria)
      const isAutonomoRow =
        regimenTag === 'AUTONOMO' ||
        tipoTag.includes('autonom') ||
        tipoTag.includes('mono')
      const requiresMacklerReview = isAutonomoRow
      // First-class regime fields
      const regimenContributivo = String(r.regimenContributivo || r.regimen || '').toUpperCase().trim()
      const regimenEspecial = String(r.regimenEspecial || '').trim().toLowerCase()
      const tipoActividad = String(r.tipoActividad || r.tipo || r.tipoRaw || '').trim()
      return {
        ...r,
        regimenContributivo: regimenContributivo || 'COMUN',
        regimenEspecial: regimenEspecial || '',
        tipoActividad: tipoActividad || '',
        rowId: String(r.rowId || `row-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`),
        periodoKey: key,
        periodoISO: key || String(r.periodoISO || ''),
        periodo: key ? `${key.slice(5, 7)}/${key.slice(0, 4)}` : String(r.periodo || ''),
        empresa: empresa || 'SIN_DATO',
        razonSocial: empresa || 'SIN_DATO',
        cuit: cuit || 'SIN_DATO',
        monto: Number.isFinite(monto) ? monto : 0,
        montoDeclarado: Number.isFinite(montoDeclarado) ? montoDeclarado : 0,
        remImpSS: Number.isFinite(monto) ? monto : 0,
        montoOriginal: Number.isFinite(monto) ? monto : 0,
        incluyeSac,
        sacManual: Number.isFinite(sacManual) ? sacManual : 0,
        sacMonto: Number(r.sacMonto || 0),
        sacAplicado: Boolean(r.sacAplicado),
        revisionReasons,
        amountSource,
        reviewSeverity,
        diasOrigen,
        diasComputables,
        reglaAplicadaDias,
        isMoratoriaRow,
        isAutonomoRow,
        requiresMacklerReview,
      }
    })
    .filter((r) => r.periodoKey)
    .sort((a, b) => String(b.periodoKey).localeCompare(String(a.periodoKey)))
}

/**
 * Construye la lista completa de filas incluyendo gaps (periodos sin
 * registro en origen) cuando se proporciona el contexto de actividad.
 *
 * @param {Array} inputRows — filas ya normalizadas
 * @param {object} [options] — { visualOnly }
 * @param {object} [context] — { actividadFuentes, expedienteFechaCese, fechaCeseCabeceraPdf }
 * @returns {Array} — filas con gaps interpolados
 */
export function buildMissingRows(inputRows, options = {}, context = {}) {
  const visualOnly = options.visualOnly !== false
  const normalized = normalizeReviewRows(inputRows)
  if (!normalized.length) return []

  const { actividadFuentes = { autonomos: [], desempleo: [] }, expedienteFechaCese = '', fechaCeseCabeceraPdf = '' } = context

  const existingKeys = new Set(normalized.map((r) => String(r.periodoKey || '')).filter(Boolean))
  const dependenciaKeys = new Set(
    normalized
      .filter((r) => Number(r.monto || 0) > 0)
      .map((r) => String(r.periodoKey || ''))
      .filter(Boolean),
  )
  const autonomoKeys = toActividadKeySet(actividadFuentes.autonomos)
  const desempleoKeys = toActividadKeySet(actividadFuentes.desempleo)

  const ceseUsuarioYm = toYmNum(String(expedienteFechaCese || '').slice(0, 7))
  const ceseCabeceraYm = toYmNum(fechaCeseCabeceraPdf)
  const maxDependenciaYm = Math.max(0, ...Array.from(dependenciaKeys).map((k) => toYmNum(k)))
  const autonomosConAporteYm = getActividadYm(actividadFuentes.autonomos, { requirePositive: true, requireValidated: true })
  const maxAutonomoConAporteYm = autonomosConAporteYm.length ? Math.max(...autonomosConAporteYm) : 0

  const puntoDeCorteYm = Math.max(ceseUsuarioYm || 0, ceseCabeceraYm || 0, maxDependenciaYm || 0, maxAutonomoConAporteYm || 0)
  const limiteVisualYm = Math.max(ceseUsuarioYm || 0, maxDependenciaYm || 0)
  if (!puntoDeCorteYm) return normalized

  const minDependenciaYm = Math.min(...Array.from(dependenciaKeys).map((k) => toYmNum(k)).filter(Boolean))
  const minAutonomoYm = Math.min(...getActividadYm(actividadFuentes.autonomos, { requireValidated: true }).filter(Boolean))
  const minDesempleoYm = Math.min(...getActividadYm(actividadFuentes.desempleo).filter(Boolean))
  const minActividadYm = Math.min(
    Number.isFinite(minDependenciaYm) ? minDependenciaYm : Number.MAX_SAFE_INTEGER,
    Number.isFinite(minAutonomoYm) ? minAutonomoYm : Number.MAX_SAFE_INTEGER,
    Number.isFinite(minDesempleoYm) ? minDesempleoYm : Number.MAX_SAFE_INTEGER,
  )

  const timelineStartYm = Number.isFinite(minActividadYm) && minActividadYm !== Number.MAX_SAFE_INTEGER ? minActividadYm : puntoDeCorteYm

  const timelineKeys = []
  let cursorYm = timelineStartYm
  while (cursorYm <= puntoDeCorteYm) {
    const key = ymNumToKey(cursorYm)
    if (key) timelineKeys.push(key)
    const y = Number(String(cursorYm).slice(0, 4))
    const m = Number(String(cursorYm).slice(4, 6))
    const nextM = m + 1
    cursorYm = nextM > 12 ? Number(`${y + 1}01`) : Number(`${y}${String(nextM).padStart(2, '0')}`)
  }

  const boundedNormalized = normalized.filter((r) => toYmNum(r.periodoKey) <= puntoDeCorteYm)

  const out = [...boundedNormalized]
  timelineKeys.forEach((k) => {
    const ym = toYmNum(k)
    if (existingKeys.has(k)) return
    const hasDependencia = dependenciaKeys.has(k)
    const hasAutonomo = autonomoKeys.has(k)
    const hasDesempleo = desempleoKeys.has(k)
    if (!hasDependencia) {
      const shouldMarkMissing = !hasAutonomo && !hasDesempleo
      const outOfVisualScope = Boolean(shouldMarkMissing && limiteVisualYm && ym > limiteVisualYm)
      if (visualOnly && outOfVisualScope) return
      out.push({
        rowId: `gap-${k}`,
        periodoKey: k,
        periodoISO: k,
        periodo: `${k.slice(5, 7)}/${k.slice(0, 4)}`,
        empresa: labelActividadMes(hasAutonomo, hasDesempleo),
        razonSocial: labelActividadMes(hasAutonomo, hasDesempleo),
        cuit: 'SIN_DATO',
        monto: 0,
        remImpSS: 0,
        montoOriginal: 0,
        tipoActividad: hasAutonomo || hasDesempleo ? 'actividad_no_dependencia' : 'sin_actividad',
        regimenContributivo: 'COMUN',
        regimenEspecial: '',
        hasAutonomo,
        hasDesempleo,
        missingInSource: shouldMarkMissing,
        outOfVisualScope,
      })
    }
  })
  return out.sort((a, b) => String(b.periodoKey || '').localeCompare(String(a.periodoKey || '')))
}

/* ------------------------------------------------------------------ */
/*  Hook: useCaseData                                                  */
/* ------------------------------------------------------------------ */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react'

const safe = (n) => Number.isFinite(n) ? n : 0

const EMPTY_EXPEDIENTE = {
  nombre: '',
  apellido: '',
  cuilCuit: '',
  fechaNacimiento: '',
  sexo: 'F',
  cantidadHijos: '',
  hijosAdoptivos: '',
  hijosDiscapacidad: '',
  cobraAUH: 'no',
  fechaCese: '',
  fallo: 'ISBIC',
  haberActual: '',
}

const isSacMonth = (key) => {
  const txt = String(key || '')
  return txt.endsWith('-06') || txt.endsWith('-12')
}

const computeRowWithSac = (row) => {
  const declarado = safe(Number(row.montoDeclarado ?? row.monto ?? row.remImpSS ?? 0))
  const incluyeSac = Boolean(row.incluyeSac)
  const aplicaSac = incluyeSac && isSacMonth(row.periodoKey)
  const sacManual = safe(Number(row.sacManual || 0))
  const sacMonto = aplicaSac ? (sacManual > 0 ? sacManual : declarado / 12) : 0
  const computable = Math.max(0, declarado - sacMonto)
  return {
    ...row,
    montoDeclarado: Number(declarado.toFixed(2)),
    sacMonto: Number(sacMonto.toFixed(2)),
    sacAplicado: aplicaSac,
    monto: Number(computable.toFixed(2)),
    remImpSS: Number(computable.toFixed(2)),
    montoOriginal: Number(computable.toFixed(2)),
  }
}

export function useCaseData({
  onRowsMutated,
  onMutationLog,
  actividadFuentes: externalActividadFuentes,
  fechaCeseCabeceraPdf: externalFechaCeseCabeceraPdf,
} = {}) {
  // ── Refs for external deps (kept current without re-creating callbacks) ──
  const actividadFuentesRef = useRef(externalActividadFuentes)
  const fechaCeseCabeceraPdfRef = useRef(externalFechaCeseCabeceraPdf)

  useEffect(() => { actividadFuentesRef.current = externalActividadFuentes }, [externalActividadFuentes])
  useEffect(() => { fechaCeseCabeceraPdfRef.current = externalFechaCeseCabeceraPdf }, [externalFechaCeseCabeceraPdf])

  // ── Core state ──
  const [expediente, setExpediente] = useState(() => {
    try {
      const saved = localStorage.getItem('expedienteData')
      if (saved) {
        const parsed = JSON.parse(saved)
        if (parsed && typeof parsed === 'object') return { ...EMPTY_EXPEDIENTE, ...parsed }
      }
    } catch { /* ignore corrupted data */ }
    return { ...EMPTY_EXPEDIENTE }
  })

  const [rows, setRows] = useState([])
  const [rawRows, setRawRows] = useState([])
  const [rowsEditadas, setRowsEditadas] = useState(null)

  // ── Ref for latest rows (avoids stale closures in callbacks) ──
  const rowsRef = useRef(rows)
  useEffect(() => { rowsRef.current = rows }, [rows])

  // ── localStorage persistence for expediente ──
  const hasExpedienteData = expediente.nombre || expediente.apellido || expediente.cuilCuit
  useEffect(() => {
    if (hasExpedienteData) {
      localStorage.setItem('expedienteData', JSON.stringify(expediente))
    }
  }, [hasExpedienteData, expediente])

  // ── updateRowField ──
  // Aplica un cambio de campo a una fila específica, materializando
  // filas virtuales (gap) si es necesario. No incluye setCalculoReady
  // (eso se maneja via onRowsMutated).
  const updateRowField = useCallback((rowId, field, value, meta) => {
    // Capture original value BEFORE the update for audit logging
    const originalRow = rowsRef.current.find((r) => String(r.rowId) === String(rowId))
    const originalValue = originalRow ? originalRow[field] : undefined

    setRows((prev) => {
      const targetId = String(rowId || '')
      const hasTarget = prev.some((r) => String(r.rowId || '') === targetId)
      const withTarget = hasTarget
        ? prev
        : (() => {
            const context = {
              actividadFuentes: actividadFuentesRef.current,
              expedienteFechaCese: '',
              fechaCeseCabeceraPdf: fechaCeseCabeceraPdfRef.current,
            }
            const virtual = buildMissingRows(prev, {}, context).find((r) => String(r.rowId || '') === targetId)
            if (!virtual) return prev
            return [...prev, {
              ...virtual,
              rowId: targetId,
              manualAdded: true,
              manualEdited: true,
              missingInSource: true,
            }]
          })()

      return normalizeReviewRows(withTarget.map((r) => {
        if (String(r.rowId || '') !== targetId) return r
        if (field === 'periodo') {
          const raw = String(value || '')
          const iso = /^\d{4}-\d{2}$/.test(raw) ? raw : String(r.periodoISO || r.periodoKey || '')
          const human = /^\d{4}-\d{2}$/.test(iso) ? `${iso.slice(5, 7)}/${iso.slice(0, 4)}` : String(r.periodo || '')
          return computeRowWithSac({ ...r, periodo: human, periodoISO: iso, periodoKey: iso })
        }
        if (field === 'monto' || field === 'montoDeclarado') {
          const n = Number(value || 0)
          return computeRowWithSac({ ...r, montoDeclarado: n })
        }
        if (field === 'incluyeSac') {
          return computeRowWithSac({ ...r, incluyeSac: Boolean(value) })
        }
        if (field === 'sacManual') {
          return computeRowWithSac({ ...r, sacManual: Number(value || 0) })
        }
        return computeRowWithSac({ ...r, [field]: value })
      }))
    })
    onRowsMutated?.()

    // Log mutation if audit metadata is provided (auto-log from wrapper)
    if (meta?.reasonCode && onMutationLog) {
      onMutationLog({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        rowId: String(rowId),
        periodo: String(originalRow?.periodoKey || originalRow?.periodo || ''),
        field: String(field),
        originalValue,
        newValue: value,
        reasonCode: String(meta.reasonCode),
        reasonDetail: String(meta.reasonDetail || ''),
        timestamp: new Date().toISOString(),
        sourcePage: 4,
      })
    }
  }, [onRowsMutated, onMutationLog])

  // ── addRevisionRow ──
  const addRevisionRow = useCallback(() => {
    setRows((prev) => {
      const base = normalizeReviewRows(prev)
      const latestKey = base
        .map((r) => String(r.periodoKey || ''))
        .filter(Boolean)
        .sort()
        .pop()
      const nextKey = latestKey || `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
      const next = [
        ...base,
        {
          periodoKey: nextKey,
          periodoISO: nextKey,
          periodo: `${nextKey.slice(5, 7)}/${nextKey.slice(0, 4)}`,
          empresa: 'CARGA MANUAL',
          razonSocial: 'CARGA MANUAL',
          cuit: 'SIN_DATO',
          monto: 0,
          montoDeclarado: 0,
          remImpSS: 0,
          montoOriginal: 0,
          missingInSource: true,
          manualAdded: true,
          incluyeSac: false,
          sacManual: 0,
          sacMonto: 0,
          sacAplicado: false,
        },
      ]
      return normalizeReviewRows(next)
    })
    onRowsMutated?.()
  }, [onRowsMutated])

  // ── removeRevisionRow ──
  const removeRevisionRow = useCallback((rowId) => {
    setRows((prev) => normalizeReviewRows(prev.filter((r) => String(r.rowId || '') !== String(rowId || ''))))
    onRowsMutated?.()
  }, [onRowsMutated])

  // ── mirrorRows (comparación origen vs sistema) ──
  const mirrorRows = useMemo(() => {
    const sys = buildMissingRows(rows, {}, {
      actividadFuentes: externalActividadFuentes || { autonomos: [], desempleo: [] },
      expedienteFechaCese: expediente.fechaCese || '',
      fechaCeseCabeceraPdf: externalFechaCeseCabeceraPdf || '',
    })
    const rawNormalized = normalizeReviewRows(rawRows)
    const rawByKey = new Map()
    rawNormalized.forEach((r) => {
      const k = String(r.periodoKey || '')
      if (!rawByKey.has(k)) rawByKey.set(k, r)
    })
    return sys.map((r) => {
      const raw = rawByKey.get(String(r.periodoKey || '')) || null
      return {
        periodoKey: r.periodoKey,
        raw,
        system: r,
        changedEmpresa: raw ? String(raw.empresa || '').trim() !== String(r.empresa || '').trim() : false,
        changedCuit: raw ? String(raw.cuit || '').trim() !== String(r.cuit || '').trim() : false,
        changedMonto: raw ? Number(raw.monto || 0) !== Number(r.monto || 0) : false,
      }
    })
  }, [rows, rawRows, externalActividadFuentes, externalFechaCeseCabeceraPdf, expediente.fechaCese])

  // ── resetCoreCaseSession ──
  const resetCoreCaseSession = useCallback(() => {
    setExpediente({ ...EMPTY_EXPEDIENTE })
    setRows([])
    setRawRows([])
    setRowsEditadas(null)
    localStorage.removeItem('expedienteData')
  }, [])

  // ── isSacMonth helper (used externally) ──
  const checkIsSacMonth = useCallback((key) => isSacMonth(key), [])

  return {
    // Core state
    expediente,
    setExpediente,
    rows,
    setRows,
    rawRows,
    setRawRows,
    rowsEditadas,
    setRowsEditadas,

    // Pure normalizer functions (re-exported for convenience)
    normalizeReviewRows,
    buildMissingRows,
    normalizePeriodoKey,
    parseMontoValue,
    toYmNum,
    ymNumToKey,
    toPeriodoKeyFromAny,
    toActividadKeySet,
    getActividadYm,
    labelActividadMes,
    extractPdfHeaderCese,
    toFechaInputFromPeriodoKey,
    isSacMonth: checkIsSacMonth,

    // Row operations
    updateRowField,
    addRevisionRow,
    removeRevisionRow,

    // Derived state
    mirrorRows,

    // Reset
    resetCoreCaseSession,
  }
}
